import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircleIcon, 
  ArrowRightIcon,
  RocketLaunchIcon,
  UsersIcon,
  DocumentTextIcon,
  ChartBarIcon,
  EnvelopeIcon,
  PhoneIcon,
  StarIcon,
  Cog6ToothIcon,
  BookOpenIcon,
  QuestionMarkCircleIcon,
  LinkIcon,
  PuzzlePieceIcon,
  ArrowPathIcon,
  PaintBrushIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import { useTenantColors } from '../contexts/TenantBrandingContext';

interface GuideStep {
  id: string;
  title: string;
  description: string;
  action: {
    label: string;
    href: string;
    icon: React.ReactNode;
  };
  checklist: string[];
  isSectionHeader?: boolean;
  planRequired?: 'growth' | 'agency';
}

const UserGuidePage: React.FC = () => {
  const navigate = useNavigate();
  const { primary } = useTenantColors();
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

  const toggleStep = (stepId: string) => {
    const newCompleted = new Set(completedSteps);
    if (newCompleted.has(stepId)) {
      newCompleted.delete(stepId);
    } else {
      newCompleted.add(stepId);
    }
    setCompletedSteps(newCompleted);
  };

  const steps: GuideStep[] = [
    {
      id: 'setup',
      title: 'Complete Your Account Setup',
      description: 'Configure your account settings and verify your email address to get started.',
      action: {
        label: 'Go to Settings',
        href: '/settings',
        icon: <Cog6ToothIcon className="w-5 h-5" />
      },
      checklist: [
        'Verify your email address',
        'Add your company information',
        'Set up your notification preferences',
        'Configure your timezone'
      ]
    },
    {
      id: 'contacts',
      title: 'Import Your Contacts',
      description: 'Add your customer contacts so you can send campaigns and collect feedback.',
      action: {
        label: 'Manage Contacts',
        href: '/contacts',
        icon: <UsersIcon className="w-5 h-5" />
      },
      checklist: [
        'Import contacts from CSV',
        'Or manually add contacts one by one',
        'Verify contact information is accurate',
        'Organize contacts with tags if needed'
      ]
    },
    {
      id: 'templates',
      title: 'Create Your First Template',
      description: 'Design email or SMS templates to use in your campaigns. Personalize with variables like customer names.',
      action: {
        label: 'Create Template',
        href: '/templates/new',
        icon: <DocumentTextIcon className="w-5 h-5" />
      },
      checklist: [
        'Choose email or SMS template type',
        'Write your message content',
        'Use variables like {{first_name}} for personalization',
        'Preview how it will look',
        'Save your template'
      ]
    },
    {
      id: 'campaign',
      title: 'Create and Send Your First Campaign',
      description: 'Launch your first campaign to collect reviews from your customers.',
      action: {
        label: 'Create Campaign',
        href: '/campaigns/new',
        icon: <EnvelopeIcon className="w-5 h-5" />
      },
      checklist: [
        'Select a template or create a new one',
        'Choose which contacts to send to',
        'Set the send date and time',
        'Preview your campaign',
        'Launch and track results'
      ]
    },
    {
      id: 'rating',
      title: 'Set Up Your Rating Gate',
      description: 'Configure how ratings are collected and routed - happy customers go to review sites, others provide feedback.',
      action: {
        label: 'Configure Rating Gate',
        href: '/rating',
        icon: <StarIcon className="w-5 h-5" />
      },
      checklist: [
        'Connect your Google Business Profile',
        'Set up Facebook and Yelp (optional)',
        'Configure rating thresholds (e.g., 4+ stars → reviews)',
        'Set up feedback form for lower ratings',
        'Test the rating gate flow'
      ]
    },
    {
      id: 'feedback',
      title: 'Manage Customer Feedback',
      description: 'Respond to customer feedback and track your response times with SLA management.',
      action: {
        label: 'View Feedback',
        href: '/feedback',
        icon: <QuestionMarkCircleIcon className="w-5 h-5" />
      },
      checklist: [
        'Review incoming feedback',
        'Assign feedback to team members',
        'Respond to customers',
        'Track SLA compliance',
        'Resolve feedback items'
      ]
    },
    {
      id: 'analytics',
      title: 'Track Your Performance',
      description: 'Monitor your campaign performance, ratings, and feedback metrics.',
      action: {
        label: 'View Analytics',
        href: '/analytics',
        icon: <ChartBarIcon className="w-5 h-5" />
      },
      checklist: [
        'View campaign statistics',
        'Monitor rating distributions',
        'Track feedback response times',
        'Export reports if needed'
      ]
    },
    // Section Header: Automation & Integrations
    {
      id: 'section-automation',
      title: '🚀 Automation & Integrations',
      description: 'Connect FeedbackGate to your existing tools and automate workflows. These features require a Growth+ or Agency plan.',
      action: {
        label: 'View Plans',
        href: '/billing',
        icon: <RocketLaunchIcon className="w-5 h-5" />
      },
      checklist: [],
      isSectionHeader: true,
      planRequired: undefined
    },
    {
      id: 'automation-webhooks',
      title: 'Set Up Automation Webhooks (Data In)',
      description: 'Automatically create contacts and trigger campaigns when data comes from external systems like Zapier, Make, or your custom app.',
      action: {
        label: 'Set Up Automation',
        href: '/settings/automations',
        icon: <LinkIcon className="w-5 h-5" />
      },
      checklist: [
        'Go to Settings → Automations',
        'Click "Set Up Your First Automation" or "Add Another Automation"',
        'Choose a template that matches your business type (Dental, Salon, etc.)',
        'Or create custom field mapping for your data source',
        'Copy your webhook URL and secret key',
        'Connect your data source (Zapier, Make, custom app, etc.)',
        'Test with sample data to verify mapping works',
        'Set up auto-enqueue campaign (optional - automatically start campaigns for new contacts)',
        'Monitor webhook activity and metrics in the dashboard',
        'View webhook events and troubleshoot any issues'
      ],
      planRequired: 'growth'
    },
    {
      id: 'zapier-integration',
      title: 'Connect to Zapier (Data Out)',
      description: 'Send FeedbackGate data to 1,000+ apps like Slack, Google Sheets, HubSpot, and more. Get notifications, sync contacts, and automate workflows.',
      action: {
        label: 'Get API Key',
        href: '/settings/api-keys',
        icon: <PuzzlePieceIcon className="w-5 h-5" />
      },
      checklist: [
        'Go to Settings → API Keys',
        'Click "Create API Key"',
        'Give it a name (e.g., "Zapier Integration")',
        'Copy your API key (you\'ll only see it once!)',
        'Go to Zapier.com and sign in or create an account',
        'Click "Create Zap" (the big orange button)',
        'Search for "FeedbackGate" in the trigger or action app',
        'Select FeedbackGate and connect using your API key',
        'Choose a trigger (New Contact, New Feedback, etc.)',
        'Set up your action (Slack, Google Sheets, CRM, etc.)',
        'Test your Zap with sample data',
        'Activate your Zap and start automating!'
      ],
      planRequired: 'growth'
    },
    {
      id: 'sequences',
      title: 'Create Multi-Step Sequences',
      description: 'Send automated follow-up messages over time with sequences. Perfect for welcome series, onboarding, or multi-touch campaigns.',
      action: {
        label: 'Create Sequence',
        href: '/sequences/new',
        icon: <ArrowPathIcon className="w-5 h-5" />
      },
      checklist: [
        'Go to Sequences page',
        'Click "Create New Sequence"',
        'Give your sequence a name and description',
        'Add your first step (email or SMS template)',
        'Add additional steps with delays between them',
        'Configure conditions and filters for each step',
        'Set up triggers (when to start the sequence)',
        'Preview the sequence flow',
        'Test the sequence with a sample contact',
        'Assign sequence to contacts or campaigns',
        'Monitor sequence performance and engagement'
      ]
    },
    // Section Header: Advanced Features
    {
      id: 'section-advanced',
      title: '⚡ Advanced Features',
      description: 'Take your reputation management to the next level with these powerful features.',
      action: {
        label: 'Explore Features',
        href: '/settings',
        icon: <RocketLaunchIcon className="w-5 h-5" />
      },
      checklist: [],
      isSectionHeader: true,
      planRequired: undefined
    },
    {
      id: 'team-management',
      title: 'Invite Team Members',
      description: 'Collaborate with your team by inviting members and managing permissions. Assign feedback, share access, and work together.',
      action: {
        label: 'Manage Team',
        href: '/team',
        icon: <UsersIcon className="w-5 h-5" />
      },
      checklist: [
        'Go to Team page',
        'Click "Invite Team Member"',
        'Enter team member\'s email address',
        'Select their role (Admin, Member, or Viewer)',
        'Review permissions for each role',
        'Send invitation email',
        'Team member receives email and accepts invitation',
        'Team member can now access the account based on their role',
        'Manage team members: edit roles, resend invitations, or remove members'
      ]
    },
    {
      id: 'branding',
      title: 'Customize Your Branding',
      description: 'Make FeedbackGate look like your brand with custom logos, colors, fonts, and domains. Growth+ plans can customize visual branding, Agency plans can remove FeedbackGate branding entirely.',
      action: {
        label: 'Customize Branding',
        href: '/branding',
        icon: <PaintBrushIcon className="w-5 h-5" />
      },
      checklist: [
        'Go to Branding page',
        'Upload your company logo (appears in upper left corner)',
        'Set your brand colors (primary, secondary, accent)',
        'Choose fonts and text colors',
        'Add custom CSS for advanced styling (optional)',
        'Configure email branding (logo, colors, footer)',
        'Set favicon URL (icon in browser tabs)',
        'Preview your branding changes',
        'Save your branding settings',
        'For Agency plans: Set up custom domain and remove FeedbackGate branding'
      ],
      planRequired: 'growth'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-4">
            <RocketLaunchIcon className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome to FeedbackGate!
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Follow this step-by-step guide to get your reputation management system up and running.
            Complete each step to build a complete workflow.
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-gray-900">Your Progress</h2>
            <span className="text-sm font-medium text-gray-600">
              {completedSteps.size} of {steps.length} steps completed
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all duration-300"
              style={{
                width: `${(completedSteps.size / steps.length) * 100}%`,
                backgroundColor: primary || '#6366f1'
              }}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-6">
          {steps.map((step, index) => {
            const isCompleted = completedSteps.has(step.id);
            // Calculate step number excluding section headers
            const stepNumber = steps
              .slice(0, index)
              .filter(s => !s.isSectionHeader).length + 1;
            
            // Section Header Rendering
            if (step.isSectionHeader) {
              return (
                <div
                  key={step.id}
                  className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border-2 border-indigo-200 p-6 mt-8"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        {step.title}
                      </h2>
                      <p className="text-gray-700">
                        {step.description}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(step.action.href)}
                      className="ml-4 px-4 py-2 rounded-md text-sm font-medium text-indigo-700 bg-white border border-indigo-300 hover:bg-indigo-50 transition-colors inline-flex items-center"
                    >
                      {step.action.icon}
                      <span className="ml-2">{step.action.label}</span>
                    </button>
                  </div>
                </div>
              );
            }
            
            // Regular Step Rendering
            return (
              <div
                key={step.id}
                className={`bg-white rounded-lg shadow-sm border-2 transition-all ${
                  isCompleted ? 'border-green-200' : 'border-gray-200'
                }`}
              >
                <div className="p-6">
                  {/* Step Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                        isCompleted
                          ? 'bg-green-100 text-green-600'
                          : 'bg-indigo-100 text-indigo-600'
                      }`}>
                        {isCompleted ? (
                          <CheckCircleIcon className="w-6 h-6" />
                        ) : (
                          stepNumber
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-xl font-semibold text-gray-900">
                            {step.title}
                          </h3>
                          {step.planRequired && (
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              step.planRequired === 'agency'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {step.planRequired === 'agency' ? 'Agency' : 'Growth+'}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600">
                          {step.description}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleStep(step.id)}
                      className={`ml-4 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isCompleted
                          ? 'bg-green-50 text-green-700 hover:bg-green-100'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {isCompleted ? 'Completed' : 'Mark Complete'}
                    </button>
                  </div>

                  {/* Checklist */}
                  {step.checklist.length > 0 && (
                    <div className="ml-14 mb-4">
                      <ul className="space-y-2">
                        {step.checklist.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start text-sm text-gray-600">
                            <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-gray-300 mr-2 mt-0.5 flex items-center justify-center">
                              {isCompleted && (
                                <CheckCircleIcon className="w-3 h-3 text-green-600" />
                              )}
                            </div>
                            <span className={isCompleted ? 'line-through text-gray-400' : ''}>
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Action Button */}
                  <div className="ml-14">
                    <button
                      onClick={() => {
                        if (step.action.href.startsWith('http')) {
                          window.open(step.action.href, '_blank', 'noopener,noreferrer');
                        } else {
                          navigate(step.action.href);
                        }
                      }}
                      className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white transition-colors"
                      style={{ backgroundColor: primary || '#6366f1' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    >
                      {step.action.icon}
                      <span className="ml-2">{step.action.label}</span>
                      <ArrowRightIcon className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Links */}
        <div className="mt-12 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <BookOpenIcon className="w-5 h-5 mr-2" />
            Additional Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href="https://api.feedbackgate.com/documentation"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
            >
              <DocumentTextIcon className="w-6 h-6 text-indigo-600 mr-3" />
              <div>
                <div className="font-medium text-gray-900">Full Documentation</div>
                <div className="text-sm text-gray-500">Complete guides and tutorials</div>
              </div>
            </a>
            <a
              href="https://api.feedbackgate.com/documentation/integrations/zapier"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
            >
              <EnvelopeIcon className="w-6 h-6 text-indigo-600 mr-3" />
              <div>
                <div className="font-medium text-gray-900">Zapier Integration</div>
                <div className="text-sm text-gray-500">Connect to 1,000+ apps</div>
              </div>
            </a>
            <a
              href="https://api.feedbackgate.com/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
            >
              <DocumentTextIcon className="w-6 h-6 text-indigo-600 mr-3" />
              <div>
                <div className="font-medium text-gray-900">API Documentation</div>
                <div className="text-sm text-gray-500">Build custom integrations</div>
              </div>
            </a>
            <button
              onClick={() => navigate('/settings')}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-left"
            >
              <Cog6ToothIcon className="w-6 h-6 text-indigo-600 mr-3" />
              <div>
                <div className="font-medium text-gray-900">Account Settings</div>
                <div className="text-sm text-gray-500">Manage your account preferences</div>
              </div>
            </button>
          </div>
        </div>

        {/* Support Section */}
        <div className="mt-8 bg-indigo-50 rounded-lg p-6 text-center">
          <QuestionMarkCircleIcon className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
          <p className="text-gray-600 mb-4">
            Our support team is here to help you get the most out of FeedbackGate.
          </p>
          <a
            href="mailto:support@feedbackgate.com"
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default UserGuidePage;
